import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { marked, type TokenizerThis, type Tokens } from 'marked';
import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';

import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { leaveDdrWhenOff } from './guard/leave-ddr-when-off';
import { sanitizeRenderedHtml } from './utils/markdown-math';
import { authInterceptor } from './interceptor/auth.interceptor';
import { errorInterceptor } from './interceptor/error.interceptor';
import { timezoneInterceptor } from './interceptor/timezone.interceptor';
import { GlobalErrorHandler } from './providers/global-error-handler';
import { ChatShellReuseStrategy } from './providers/chat-shell-reuse.strategy';
import { ThemeService } from './theme/theme.service';

// DDR file-library artifact link (#419), in either shape the backend has ever
// written: the legacy API route (`/api/ddr/files/{id}/content`, `/content`
// optional because the model sometimes drops it) or the app route
// (`/files/{id}`, what file_chat_link writes since 2026-09-08). Relative or
// absolute; an absolute app-shaped link only counts when it is THIS origin, so
// `https://example.com/files/x` stays an ordinary link.
const DDR_FILE_API_LINK_RE = /^(?:https?:\/\/[^/]+)?\/api\/ddr\/files\/([A-Za-z0-9_.-]+)(?:\/content)?(\?[^\s"]*)?$/;
const DDR_FILE_APP_LINK_RE = /^(?:https?:\/\/([^/]+))?\/files\/([A-Za-z0-9_.-]+)(\?[^\s"]*)?$/;

/** The file id and query of a DDR artifact link, or null when `href` is not one. */
export const ddrArtifactRef = (href: string): { id: string; query: string } | null => {
    const api = DDR_FILE_API_LINK_RE.exec(href);
    if (api) {
        return { id: api[1], query: api[2] ?? '' };
    }
    const app = DDR_FILE_APP_LINK_RE.exec(href);
    if (app && (!app[1] || app[1] === window.location.host)) {
        return { id: app[2], query: app[3] ?? '' };
    }
    return null;
};

/** The API bytes URL for an artifact, for `<img src>` (a subresource fetch
 *  that rides the session cookie on the API host). Null = not a file link.
 *
 *  The query string is PRESERVED: it carries behavior, not decoration. Dropping
 *  it turned `?inline=1` links into attachment downloads, so inline images and
 *  previews silently became file downloads. */
export const ddrArtifactUrl = (href: string): string | null => {
    const ref = ddrArtifactRef(href);
    return ref ? `${environment.apiBase}/api/ddr/files/${ref.id}/content${ref.query}` : null;
};

/** The APP url for an artifact, for anything a user clicks. Same origin, so it
 *  opens inside the signed-in SPA (`/files/:id`), which fetches with the bearer
 *  token. Never the API host: a document navigation there carries only the
 *  15-minute cookie and rendered `{"detail":"Not authenticated"}` for anyone
 *  who clicked later or from an email. */
export const ddrArtifactAppUrl = (href: string): string | null => {
    const ref = ddrArtifactRef(href);
    return ref ? `/files/${ref.id}${ref.query}` : null;
};

export const markedOptionsFactory = (): MarkedOptions => {
    const renderer = new MarkedRenderer();

    renderer.link = ({ href, text }): string => {
        const fileUrl = ddrArtifactAppUrl(href ?? '');
        if (fileUrl) {
            // Artifact preview card: extension block + filename, opens the
            // app's /files/:id page in a new tab, which downloads the file.
            const name = text || 'file';
            const ext = (/\.([A-Za-z0-9]{1,8})$/.exec(name)?.[1] ?? 'file').toUpperCase();
            return (
                `<a class="artifact-file-card" target="_blank" rel="noopener noreferrer" href="${fileUrl}">` +
                `<span class="afc-ext">${ext}</span>` +
                `<span class="afc-meta"><span class="afc-name">${name}</span><span class="afc-hint">Click to download</span></span>` +
                `</a>`
            );
        }
        return `<a target="_blank" rel="noopener noreferrer" href="${href}">${text}</a>`;
    };

    renderer.image = ({ href, text }): string => {
        const fileUrl = ddrArtifactUrl(href ?? '');
        const cls = fileUrl ? ' class="artifact-inline-img"' : '';
        return `<img${cls} alt="${text ?? ''}" loading="lazy" src="${fileUrl ?? href ?? ''}" />`;
    };

    // DOMPurify-sanitize marked's HTML output globally. ciq-markdown renders with
    // [disableSanitizer]=true (so KaTeX inline styles survive Angular's sanitizer),
    // making this hook the XSS guard for that path; it also hardens every other
    // <markdown> usage. KaTeX structure is whitelisted in sanitizeRenderedHtml.
    // Require double-tilde (~~) for strikethrough. The default GFM regex accepts
    // single ~ which misrenders LLM responses that use ~ as "approximately".
    marked.use({
        tokenizer: {
            del(this: TokenizerThis, src: string) {
                const m = /^~~(?=[^\s~])([\s\S]*?[^\s~])~~(?=[^~]|$)/.exec(src);
                if (!m) {
                    return false as unknown as Tokens.Del;
                }
                return {
                    type: 'del',
                    raw: m[0],
                    text: m[1],
                    tokens: this.lexer.inlineTokens(m[1]),
                } as Tokens.Del;
            },
        },
        hooks: { postprocess: sanitizeRenderedHtml },
    });

    return {
        renderer: renderer,
        gfm: true,
        breaks: true,
        pedantic: false,
    };
};

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(appRoutes),
        // Keep the chat shell (+ its sidebar) mounted across /chat/** surface
        // navigation so clicking around the sidebar doesn't re-mount/reset it.
        { provide: RouteReuseStrategy, useClass: ChatShellReuseStrategy },
        provideHttpClient(withInterceptors([authInterceptor, timezoneInterceptor, errorInterceptor])),
        provideMarkdown({ markedOptions: { provide: MARKED_OPTIONS, useFactory: markedOptionsFactory } }),
        // Force ThemeService instantiation at bootstrap so a theme is on the DOM
        // before the first paint. The service constructor applies the default
        // (light); the user's saved theme is user-scoped, so it lands as soon as
        // AuthService restores the cached session and calls loadForUser() - which
        // it does synchronously from the cached payload, no HTTP round-trip. That
        // ordering is what avoids a light-flash for users who picked a dark theme.
        provideAppInitializer(() => {
            inject(ThemeService);
        }),
        provideAppInitializer(() => {
            leaveDdrWhenOff();
        }),
        // Service worker is registered unconditionally (not behind the
        // pwa-enabled flag): besides offline/PWA duty it is the update
        // vehicle that gets long-lived tabs off stale bundles. The
        // pwa-enabled flag still gates the install surface (manifest link
        // + push) in App. registerWhenStable defers registration so it
        // never competes with first-paint work.
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
    ],
};
